/**
 * Shared parameter utility functions for CreateScreen and related components
 * These functions are also used in ZenScreen/ControlBar
 */

export const PRIMARY = '#FA8B14';
export const PRIMARY_LIGHT = 'rgba(250, 139, 20, 0.15)';
export const PRIMARY_BORDER = 'rgba(250, 139, 20, 0.4)';

/**
 * Format parameter name to readable label
 * Handles: image_urls0 -> "Image URLs", positive_prompt -> "Positive Prompt"
 */
export const formatParamLabel = (name, title) => {
  const base = title || name;
  return base
    .replace(/[0-9]+$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
    .replace(/Url/g, 'URL')
    .replace(/Urls/g, 'URLs')
    .trim();
};

/**
 * Does this parameter NAME look like a media input?
 *
 * Name-only, deliberately WITHOUT `video`/`audio` tokens. This is the same
 * token set the codebase has always used, and it is what the negative bucket
 * filters (prompt / select / text / advanced) test against, so those buckets
 * keep excluding exactly what they exclude today — including integer params
 * like `image_quality`, which are excluded by NAME even though they are not
 * media.
 *
 * Adding `video`/`audio` here would be a regression: six models ship a
 * BOOLEAN param literally named `audio` (Seedance 2.5, Wan 2.7, LTX 2.3,
 * PixVerse V6, Seedance 2.0/2.0 Fast) and two ship a required
 * `generate_audio`. Those are soundtrack toggles, not uploads. The media KIND
 * is refined separately in classifyMediaParam, only after this returns true.
 */
export const isMediaName = (name) => {
  const n = String(name || '').toLowerCase();
  if (n.includes('webhook')) return false;
  return n.includes('image') || n.includes('photo') || n.includes('picture')
    || n.includes('mask') || n.includes('url');
};

/**
 * Classify a parameter as a media upload input, or null if it isn't one.
 *
 * Single source of truth — ZenControlPanel, ZenScreen, SettingsModal and
 * DefaultPresentation all previously carried their own inline variants with
 * DIFFERENT token sets, which is why `reference_video_urls` rendered as an
 * image picker in one place and a text field in another.
 *
 * @returns {{kind:'image'|'video'|'audio', isArray:boolean, min:number, max:number, required:boolean}|null}
 */
export const classifyMediaParam = (param) => {
  if (!param || isParamPrivate(param)) return null;

  const dt = param.data_type;
  // Only these two carry media. This gate is what keeps the `audio`/
  // `generate_audio`/`sound` booleans and `image_quality` integers out.
  if (dt !== 'string' && dt !== 'object[]') return null;

  // A string with a fixed option list is a select, not an upload
  // (e.g. `image_format`).
  if (dt === 'string' && param.allowed_values) return null;

  const n = String(param.parameter_name || '').toLowerCase();
  if (!isMediaName(n)) return null;

  const kind = n.includes('video') ? 'video' : n.includes('audio') ? 'audio' : 'image';
  const isArray = dt === 'object[]';
  const rawMax = param.allowed_max_value != null ? Number(param.allowed_max_value) : null;
  const rawMin = param.allowed_min_value != null ? Number(param.allowed_min_value) : null;

  return {
    kind,
    isArray,
    min: Number.isFinite(rawMin) ? rawMin : 0,
    max: Number.isFinite(rawMax) && rawMax > 0 ? rawMax : (isArray ? 10 : 1),
    required: isParamRequired(param),
  };
};

/** Accept attribute + human label per media kind. */
export const MEDIA_KIND_META = {
  image: { accept: 'image/*', label: 'image', article: 'an', plural: 'images' },
  video: { accept: 'video/*', label: 'video', article: 'a', plural: 'videos' },
  audio: { accept: 'audio/*', label: 'audio file', article: 'an', plural: 'audio files' },
};

/**
 * Extension → kind. Mirrors the server allowlists in uro-api
 * (imageUploadRoute / videoUploadRoute / audioUploadRoute) so a file the API
 * would accept is never rejected client-side, and an uploaded file is never
 * invisible in job history.
 */
const EXT_KIND = {
  jpg: 'image', jpeg: 'image', png: 'image', webp: 'image', gif: 'image',
  avif: 'image', heic: 'image', heif: 'image', bmp: 'image', svg: 'image',
  mp4: 'video', webm: 'video', mov: 'video', avi: 'video', mkv: 'video',
  mp3: 'audio', wav: 'audio', m4a: 'audio', aac: 'audio', flac: 'audio',
  ogg: 'audio', weba: 'audio', opus: 'audio',
};

const extOf = (nameOrUrl) => {
  const s = String(nameOrUrl || '').toLowerCase().split(/[?#]/)[0];
  const dot = s.lastIndexOf('.');
  return dot === -1 ? '' : s.slice(dot + 1);
};

/**
 * Which media kind is this File? Returns 'image'|'video'|'audio'|null.
 *
 * Falls back to the extension when the browser reports an empty `file.type`,
 * which happens routinely for .mkv/.m4a/.flac/.weba and for files dragged from
 * archives or network mounts. Without the fallback those legitimate uploads are
 * rejected by the kind filter.
 */
export const mediaKindOfFile = (file) => {
  const t = String(file?.type || '');
  if (t.startsWith('image/')) return 'image';
  if (t.startsWith('video/')) return 'video';
  if (t.startsWith('audio/')) return 'audio';
  return EXT_KIND[extOf(file?.name)] || null;
};

/**
 * Media kind of a URL, by extension then by upload directory.
 * Defaults to 'image' because historic job inputs are overwhelmingly images
 * and were previously labelled that way unconditionally.
 */
export const mediaTypeOfUrl = (url) => {
  const u = String(url || '').toLowerCase();
  const byExt = EXT_KIND[extOf(u)];
  if (byExt) return byExt;
  if (u.includes('/client-videos/')) return 'video';
  if (u.includes('/client-audio/')) return 'audio';
  return 'image';
};

/** Does this URL point at media we can render? */
export const isRenderableMediaUrl = (url) => {
  const u = String(url || '').toLowerCase();
  return !!EXT_KIND[extOf(u)]
    || u.includes('/images/') || u.includes('/client-videos/') || u.includes('/client-audio/')
    || u.startsWith('data:image');
};

/**
 * Parse condition_rule from parameter (handles string or object)
 */
export const parseConditionRule = (rule) => {
  if (!rule) return null;
  if (typeof rule === 'string') {
    try { return JSON.parse(rule); } catch { return null; }
  }
  return rule;
};

/**
 * Check if a value is "set" (not null, undefined, empty string, or empty array)
 */
export const hasValue = (value) => {
  if (value === null || value === undefined || value === '' || value === 'null') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

/**
 * Calculate disabled parameters based on condition rules
 * @param {Array} parameters - Array of parameter definitions
 * @param {Object} textInputs - Current text input values
 * @param {Object} uploadedImages - Current uploaded image values
 * @returns {Object} Map of paramName -> { reason, disabledBy }
 */
export const calculateDisabledParams = (parameters, textInputs = {}, uploadedImages = {}) => {
  const disabled = {};
  
  if (!parameters) return disabled;
  
  parameters.forEach(param => {
    const rule = parseConditionRule(param.condition_rule);
    if (!rule) return;
    
    const paramName = param.parameter_name;
    const paramValue = textInputs?.[paramName] || uploadedImages?.[paramName];
    const paramHasValue = hasValue(paramValue);
    
    // Handle mutually_exclusive_with
    if (rule.mutually_exclusive_with && paramHasValue) {
      rule.mutually_exclusive_with.forEach(exclusiveParam => {
        disabled[exclusiveParam] = {
          reason: `Cannot be used with "${param.parameter_title || paramName}"`,
          disabledBy: paramName,
        };
      });
    }
    
    // Handle restricts_when_present
    if (rule.restricts_when_present && paramHasValue) {
      Object.entries(rule.restricts_when_present).forEach(([targetParam, restriction]) => {
        let allowedValues = restriction.allowed_values;
        if (typeof allowedValues === 'string') {
          try { allowedValues = JSON.parse(allowedValues); } catch { /* ignore */ }
        }
        // Store restriction info but don't fully disable
        if (!disabled[targetParam]) {
          disabled[targetParam] = {
            restrictedValues: allowedValues,
            restrictedBy: paramName,
          };
        }
      });
    }
  });
  
  return disabled;
};

/**
 * Parse allowed values from parameter definition
 * @param {any} allowedValues - Raw allowed_values from parameter
 * @returns {Array} Parsed array of allowed values
 */
export const parseAllowedValues = (allowedValues) => {
  if (!allowedValues) return [];
  if (Array.isArray(allowedValues)) return allowedValues;
  if (typeof allowedValues === 'string') {
    try {
      const parsed = JSON.parse(allowedValues);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return allowedValues.split(',').map(v => v.trim());
    }
  }
  return [allowedValues];
};

/**
 * Get parameter value with fallback to default
 */
export const getParamValue = (paramName, textInputs, settings, defaultValue) => {
  return textInputs?.[paramName] ?? settings?.[paramName] ?? defaultValue ?? '';
};

/**
 * Check if parameter is required
 */
export const isParamRequired = (param) => {
  return Number(param.is_required) === 1 || param.is_required === true;
};

/**
 * Check if parameter is private
 */
export const isParamPrivate = (param) => {
  return param.is_private || Number(param.is_private) === 1;
};

/**
 * Filter out private parameters
 */
export const filterPublicParams = (parameters) => {
  if (!parameters) return [];
  return parameters.filter(p => !isParamPrivate(p));
};
